import { Env, addEventListener, dispatchAppEvent } from '@voghion/fe-util';
import { formatCallOptions, precheckWrapper, setMircoAppApiChecker, runTask, defaultTimeout } from './config.mjs';
import { isMicroApp } from './env.mjs';
import EventBus from './event-bus.mjs';
import { CHECK_API, EVENT_HYBRID_SERVER_READY, EVENT_CHECK_HYBRID_SERVER, EVENT_BACK_INVOKED, EVENT_VENDOR_CALLBACK, EVENT_BACK_TO_TOP, EVENT_MAGIC, EVENT_APPLE_PAY_RESULT, EVENT_KEYBOARD_RECT_CHANGE, EVENT_GOOGLE_PAY_RESULT } from './constant.mjs';
import { generateUUID } from './helper.mjs';
/**
 * 单个 webView 事件
 */
const singleWebViewEvents = [
    EVENT_VENDOR_CALLBACK,
    EVENT_BACK_INVOKED,
    EVENT_BACK_TO_TOP,
    EVENT_MAGIC,
    EVENT_APPLE_PAY_RESULT,
    EVENT_GOOGLE_PAY_RESULT,
    EVENT_KEYBOARD_RECT_CHANGE
];
// iframe 通信响应
const iframeResponseCallbacks = {};
const iframeHandlers = {};
let uniqueId = 1;
const minTimeout = 1000;
const customWindow = typeof window !== 'undefined' ? window : null;
let windowLoaded = false;
const webViewQueue = [];
const env = Env.getInstance();
if (env.isClient) {
    window.addEventListener('load', () => {
        windowLoaded = true;
        setTimeout(() => {
            if (!customWindow.WebViewJavascriptBridge &&
                !customWindow.microAppJavascriptBridge) {
                for (const callback of webViewQueue) {
                    callback(null);
                }
            }
        }, minTimeout);
    });
}
// webView初始化
function setupWebViewJavascriptBridge(callback) {
    var _a, _b;
    // Android使用
    if (env.isAndroid) {
        if (customWindow.WebViewJavascriptBridge) {
            return callback(customWindow.WebViewJavascriptBridge);
        }
        document.addEventListener('WebViewJavascriptBridgeReady', () => {
            callback(customWindow.WebViewJavascriptBridge);
        }, false);
    }
    // iOS使用
    if (env.isIOS) {
        if (customWindow.WebViewJavascriptBridge) {
            return callback(customWindow.WebViewJavascriptBridge);
        }
        if (customWindow.WVJBCallbacks) {
            customWindow.WVJBCallbacks.push(callback);
        }
        else {
            customWindow.WVJBCallbacks = [callback];
            if ((_b = (_a = customWindow.webkit) === null || _a === void 0 ? void 0 : _a.messageHandlers) === null || _b === void 0 ? void 0 : _b.iOS_Native_InjectJavascript) {
                customWindow.webkit.messageHandlers.iOS_Native_InjectJavascript.postMessage(null);
            }
            else {
                const WVJBIframe = document.createElement('iframe');
                WVJBIframe.style.display = 'none';
                WVJBIframe.src = 'wvjbscheme://__BRIDGE_LOADED__';
                document.documentElement.appendChild(WVJBIframe);
                setTimeout(() => {
                    document.documentElement.removeChild(WVJBIframe);
                }, 0);
            }
        }
    }
    // 微前端环境
    if (isMicroApp()) {
        if (customWindow.microAppJavascriptBridge) {
            return callback(customWindow.microAppJavascriptBridge);
        }
        window.addEventListener('message', (e) => {
            try {
                const message = e.data;
                if (typeof message === 'object') {
                    const { callbackId, responseData, handlerName, data } = message;
                    if (callbackId) {
                        const event = iframeResponseCallbacks[callbackId];
                        if (event) {
                            // json 序列化，和客户端保持一致
                            event.callback(responseData);
                            delete iframeResponseCallbacks[callbackId];
                        }
                    }
                    else if (handlerName) {
                        const handler = iframeHandlers[handlerName];
                        handler && handler(data);
                    }
                }
            }
            catch (e) {
                console.error(e);
            }
        });
        customWindow.microAppJavascriptBridge = {
            callHandler(handlerName, data, callback) {
                const callbackId = 'cb_' + uniqueId++ + '_' + new Date().getTime();
                iframeResponseCallbacks[callbackId] = {
                    callback
                };
                window.parent.postMessage({
                    callbackId,
                    handlerName,
                    data
                }, '*');
            },
            init() {
                // 暂时用不到
            },
            registerHandler(handlerName, callback) {
                iframeHandlers[handlerName] = callback;
            }
        };
        return callback(customWindow.microAppJavascriptBridge);
    }
    if (!env.isApp() &&
        !customWindow.WebViewJavascriptBridge &&
        !customWindow.microAppJavascriptBridge) {
        if (windowLoaded) {
            // 如果不是 app 环境，模拟回调，方便调试
            // 延迟一段时间是为了避免老版本无法通过ua头检测的问题
            setTimeout(() => {
                if (!customWindow.WebViewJavascriptBridge &&
                    !customWindow.microAppJavascriptBridge) {
                    callback(null);
                }
            }, minTimeout);
        }
        else {
            webViewQueue.push(callback);
        }
    }
}
let inited = false;
// APP调js方法 （参数分别为:js提供的方法名、回调）
function registerHandler(name, callback = null) {
    setupWebViewJavascriptBridge((bridge) => {
        bridge &&
            bridge.registerHandler(name, (data, responseCallback, eventName) => {
                callback && callback(data, responseCallback, eventName);
            });
    });
}
/**
 * 接口调用，不包含环境判断
 * @param name
 * @param data
 * @returns
 */
const innerCallPromise = (name, data) => new Promise((resolve, reject) => {
    setupWebViewJavascriptBridge((bridge) => {
        bridge
            ? bridge.callHandler(name, data, (res) => {
                if (res) {
                    // 客户端响应的是 json 字符串，都预解析一下，方便使用
                    try {
                        res = JSON.parse(res);
                    }
                    catch (err) {
                        console.log(err);
                        return reject(new Error('bridge response error'));
                    }
                }
                resolve(res);
            })
            : reject(new Error('no bridge'));
    });
});
// 注册微前端接口检查器
setMircoAppApiChecker((name) => runTask({
    timeout: defaultTimeout
}, () => innerCallPromise(CHECK_API, { name })).then((res) => !!res.data));
const pageId = generateUUID();
const globalEventListener = new Map([]);
const bridge = {
    hybridServer: false,
    /**
     * 初始化
     */
    init(options = {}) {
        if (!inited) {
            inited = true;
            this.hybridServer = !!options.hybridServer;
            // 注册回调函数，第一次连接时调用 初始化函数(android需要初始化,ios不用)
            setupWebViewJavascriptBridge((bridge) => {
                const env = Env.getInstance();
                if (env.isAndroid || isMicroApp()) {
                    // 初始化，此处传递的一个默认函数，在客户端向 js 发送了未知（js 未调用 registerHandler 注册）的事件时会回调到这儿
                    // init 为同步方法，所以不需要等待响应
                    bridge &&
                        bridge.init((message, responseCallback) => {
                            if (responseCallback) {
                                const data = {
                                    'Javascript Responds': 'Wee!'
                                };
                                responseCallback(data);
                            }
                        });
                }
            });
        }
    },
    // js调APP方法, 响应promise
    callPromise(callOptions, name, data = null) {
        callOptions = formatCallOptions(callOptions);
        callOptions.apiName = name;
        if (typeof data === 'object' && '_checkMode' in data) {
            callOptions.checkMode = true;
            delete data._checkMode;
        }
        return precheckWrapper(callOptions, () => innerCallPromise(name, data));
    },
    /**
     * 绑定事件
     * @param event 事件名
     * @param func 回调
     * @param target 事件目标，默认为当前 bridge 实例
     */
    addEventListener(event, func = () => { }, target = this) {
        EventBus.on(event, target, func);
        if (this.hybridServer || singleWebViewEvents.includes(event)) {
            registerHandler(event, (res, _nativeCallback, eventName) => {
                if (typeof res === 'string') {
                    try {
                        res = JSON.parse(res);
                    }
                    catch (err) {
                        void 0;
                    }
                }
                // 当注册魔术事件后，客户端在未注册的事件回调参数里会传递实际触发的事件名
                bridge.raiseEvent(eventName || event, res);
            });
        }
        else {
            if (!globalEventListener.get(event)) {
                globalEventListener.set(event, true);
                addEventListener(event, (e) => {
                    const detail = e.detail || {};
                    if (event === EVENT_HYBRID_SERVER_READY) {
                        if (!detail.pageId || detail.pageId === pageId) {
                            bridge.raiseEvent(event, detail);
                        }
                    }
                    else {
                        bridge.raiseEvent(event, detail);
                    }
                });
            }
            // 如果是监听 hybrid server 事件，主动发起一次检测
            if (event === EVENT_HYBRID_SERVER_READY) {
                dispatchAppEvent(EVENT_CHECK_HYBRID_SERVER, {
                    pageId
                });
            }
        }
    },
    /**
     * 移除事件
     * @param event 事件名
     * @param target 事件目标，默认为当前 bridge 实例
     */
    removeEventListener(event, target = this) {
        EventBus.remove(event, target);
    },
    /**
     * 触发事件
     * @param event 事件名
     * @param data 事件参数
     */
    raiseEvent(event, data = {}) {
        EventBus.emit(event, data);
    },
    /**
     * 默认事件回调是会在 setTimeout callback 里，避免回调直接调用 alert 导致主进程卡死
     * 如果确定不会用到 alert、prompt 等会卡住 js 进程的方法时，可以关掉，这样回调是实时的
     */
    disableHandlerDelay() {
        var _a, _b;
        ;
        (_b = (_a = window.WebViewJavascriptBridge) === null || _a === void 0 ? void 0 : _a.disableJavscriptAlertBoxSafetyTimeout) === null || _b === void 0 ? void 0 : _b.call(_a);
    },
    isMicroApp
};
export default bridge;
